import styled from '@emotion/styled';
import { Input } from 'antd';
import isEmpty from 'lodash/isEmpty';
import React from 'react';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import * as S from 'src/modules/programs/stepComponents/styles';
import stepIcon from 'src/public/img/undraw-to-do-list.svg';
import { color } from 'src/shared/styles/variables';

import {
    changeFlexibilityPlanField,
    changeFlexibilityPlanInput,
    submitFlexibilityPlanData,
} from '../actions/weekFourActions';
import { PracticeQuestionHolder } from '../components/PracticeQuestionHolder';
import { ContentQuestion, OptionsQuestion, SelectedAnswer } from '../models/weekFour';
import {
    getFlexibilityPlanQuestionContent,
    getFlexibilityPlanQuestionOption,
    getFlexibilityPlanSelectedAnswers,
    isFlexibilityPlanDataValid,
} from '../selectors/weekFourSelectors';

const StyledTextArea = styled<any>(Input.TextArea)({
    '&&.ant-input': {
        marginTop: 5,
        margin: '5px 0 14px',
        padding: '11px 16px',
        resize: 'none',
        borderRadius: 4,
        border: `solid 1px ${color.paleSlate}`,
    },
});

interface StateProps {
    contentQuestion: ContentQuestion;
    optionsQuestion: OptionsQuestion;
    isDataValid: boolean;
    selectedAnswers: SelectedAnswer[];
}

interface ActionsProps {
    changeFlexibilityPlanInput: typeof changeFlexibilityPlanInput;
    changeFlexibilityPlanField: typeof changeFlexibilityPlanField;
    submitFlexibilityPlanData: typeof submitFlexibilityPlanData;
}

type Props = StepButtonsProps & StateProps & ActionsProps;

class WeekFourPlanStepContainer extends React.Component<Props> {
    public handleSubmit = () => {
        if (!this.props.isDataValid) {
            return;
        }

        this.props.submitFlexibilityPlanData(this.props.selectedAnswers);
        this.props.goToNextStep();
    };

    private handleInputChange = (id: number) => ({ target }: React.ChangeEvent<HTMLInputElement>) =>
        this.props.changeFlexibilityPlanInput({ value: target.value, id });

    private handleFieldChange = (value: string, field: string, id: number) =>
        this.props.changeFlexibilityPlanField({ value, field, id });

    private handleAdditionalInfoChange = (id: number) => (e: React.ChangeEvent<HTMLInputElement>) =>
        this.handleFieldChange(e.target.value, 'additionalAnswer', id);

    public render(): JSX.Element {
        const { contentQuestion, optionsQuestion, isDataValid, goToPreviousStep } = this.props;

        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn md={14}>
                        <S.StepHeading small>Practice:</S.StepHeading>
                        <S.StepHeading>Do It Anyway!</S.StepHeading>
                        <S.StepDescription small>
                            What do you care about that have you been avoiding because it brings up
                            a difficult emotion? What have you been telling yourself you can’t do
                            that you actually really want to do?
                        </S.StepDescription>
                        <S.PlannedActivitiesHolder>
                            <S.PlannedActivitiesHeading>
                                Planned activities:
                            </S.PlannedActivitiesHeading>
                            <S.PlannedActivitiesHeading noUnderline>
                                {contentQuestion.text}
                            </S.PlannedActivitiesHeading>
                            <StyledTextArea
                                value={contentQuestion.answer}
                                placeholder="E.g. Ask a coworker to lunch!"
                                onChange={this.handleInputChange(contentQuestion.id)}
                                autosize={{ minRows: 3, maxRows: 8 }}
                            />
                            <S.PlannedActivitiesHeading noUnderline>
                                {optionsQuestion && optionsQuestion.text}
                            </S.PlannedActivitiesHeading>
                            {!isEmpty(optionsQuestion) && (
                                <PracticeQuestionHolder
                                    key={optionsQuestion.id}
                                    question={optionsQuestion}
                                    handleAnswerChange={this.handleFieldChange}
                                    handleInputChange={this.handleAdditionalInfoChange}
                                />
                            )}
                        </S.PlannedActivitiesHolder>
                    </S.ContentColumn>
                    <S.ContentColumn md={10}>
                        <S.StepIcon iconUrl={stepIcon} />
                    </S.ContentColumn>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!isDataValid}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    contentQuestion: getFlexibilityPlanQuestionContent(state),
    optionsQuestion: getFlexibilityPlanQuestionOption(state),
    isDataValid: isFlexibilityPlanDataValid(state),
    selectedAnswers: getFlexibilityPlanSelectedAnswers(state),
});

export const WeekFourPlanStep = connect(mapStateToProps, {
    changeFlexibilityPlanInput,
    changeFlexibilityPlanField,
    submitFlexibilityPlanData,
})(WeekFourPlanStepContainer);
